import { useMemo } from 'react';
import { Button, Card } from '../../ui';
import {
  useClusteringBatchSummary,
  useClusteringClusters,
  useRegenerateBatchSummary,
} from '../../../hooks/useClustering';
import type { BatchRead, BatchSummaryPayload, ClusterRead } from '../../../types/clustering';
import { formatNumber } from '../shared';
import { userMessage } from '../../../utils/userMessage';
import styles from '../Clustering.module.css';

const OVERVIEW_CLUSTER_LIMIT = 500;
const TOP_ITEM_LIMIT = 3;

interface BatchOverviewProps {
  batch: BatchRead;
  canEdit: boolean;
}

function avg(values: Array<number | null | undefined>) {
  const clean = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return clean.length ? clean.reduce((sum, v) => sum + v, 0) / clean.length : null;
}

function pct(value: number | null | undefined) {
  return value === null || value === undefined ? '—' : `${Math.round(value)}%`;
}

function countBy(clusters: ClusterRead[], key: 'search_intent' | 'funnel_stage') {
  return clusters.reduce<Record<string, number>>((acc, cluster) => {
    const label = cluster[key] ?? 'unknown';
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});
}

function topCompetitors(clusters: ClusterRead[]) {
  const totals = new Map<string, number>();
  clusters.forEach((cluster) => {
    cluster.top_competitors.forEach((competitor) => {
      totals.set(competitor.domain, (totals.get(competitor.domain) ?? 0) + competitor.hits);
    });
  });
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_ITEM_LIMIT);
}

function payloadList(payload: BatchSummaryPayload | null | undefined, key: keyof BatchSummaryPayload) {
  const value = payload?.[key];
  return Array.isArray(value) ? value.slice(0, TOP_ITEM_LIMIT) : [];
}

export function BatchOverview({ batch, canEdit }: BatchOverviewProps) {
  const { data: clusters = [], isLoading } = useClusteringClusters(batch.id, {
    sort: 'opportunity_score',
    order: 'desc',
    include_merged: false,
    limit: OVERVIEW_CLUSTER_LIMIT,
    offset: 0,
  });
  const summaryQuery = useClusteringBatchSummary(batch.id, batch.status === 'done');
  const regenerateSummary = useRegenerateBatchSummary(batch.id);

  const overview = useMemo(() => {
    const sortedByRisk = [...clusters].sort(
      (a, b) => (b.cannibalization_risk ?? -1) - (a.cannibalization_risk ?? -1),
    );
    const sortedByAio = [...clusters].sort(
      (a, b) => (b.ai_overview_pct ?? -1) - (a.ai_overview_pct ?? -1),
    );
    const avgSize = batch.clusters_count ? batch.total_keywords / batch.clusters_count : null;
    return {
      avgSize,
      avgOpportunity: avg(clusters.map((cluster) => cluster.opportunity_score)),
      avgAio: avg(clusters.map((cluster) => cluster.ai_overview_pct)),
      topOpportunity: clusters[0],
      topRisk: sortedByRisk[0],
      topAio: sortedByAio[0],
      intents: countBy(clusters, 'search_intent'),
      funnels: countBy(clusters, 'funnel_stage'),
      competitors: topCompetitors(clusters),
    };
  }, [batch.clusters_count, batch.total_keywords, clusters]);

  const summary = summaryQuery.data;
  const payload = summary?.payload;
  const findings = payloadList(payload, 'key_findings');
  const actions = payloadList(payload, 'recommended_actions');

  return (
    <div className={styles.overviewStack}>
      <div className={styles.kpiGrid}>
        <Card variant="outlined" className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Clusters</span>
          <strong>{formatNumber(batch.clusters_count)}</strong>
          <span>{overview.avgSize ? `${overview.avgSize.toFixed(1)} keywords avg.` : '—'}</span>
        </Card>
        <Card variant="outlined" className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Avg opportunity</span>
          <strong>{pct(overview.avgOpportunity)}</strong>
          <span>{overview.topOpportunity?.cluster_label ?? 'Waiting for clusters'}</span>
        </Card>
        <Card variant="outlined" className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Highest risk</span>
          <strong>
            {pct(
              overview.topRisk?.cannibalization_risk === null ||
                overview.topRisk?.cannibalization_risk === undefined
                ? null
                : overview.topRisk.cannibalization_risk * 100,
            )}
          </strong>
          <span>{overview.topRisk?.cluster_label ?? 'No risk signal yet'}</span>
        </Card>
        <Card variant="outlined" className={styles.kpiCard}>
          <span className={styles.kpiLabel}>AI Overview avg.</span>
          <strong>{pct(overview.avgAio)}</strong>
          <span>{overview.topAio?.cluster_label ?? 'No AIO signal yet'}</span>
        </Card>
      </div>

      <Card variant="outlined" className={styles.summaryCard}>
        <div className={styles.overviewSectionHeader}>
          <div>
            <h2>AI summary</h2>
            <p>Generated after a completed clustering job; safe to regenerate when labels change.</p>
          </div>
          {canEdit && batch.status === 'done' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => regenerateSummary.mutate()}
              loading={regenerateSummary.isPending}
            >
              Regenerate
            </Button>
          )}
        </div>
        {summaryQuery.isLoading ? (
          <p className={styles.mutedText}>Loading summary…</p>
        ) : summary?.status === 'done' && payload ? (
          <div className={styles.summaryBody}>
            <p>{payload.executive_summary ?? summary.markdown ?? 'Summary generated.'}</p>
            {findings.length > 0 && <InsightList title="Key findings" items={findings} />}
            {actions.length > 0 && <InsightList title="Recommended actions" items={actions} />}
          </div>
        ) : (
          <p className={styles.mutedText}>{summaryStatusMessage(summary?.status, summary?.error_message)}</p>
        )}
      </Card>

      <div className={styles.insightGrid}>
        <InsightCard title="Intent mix" items={overview.intents} loading={isLoading} />
        <InsightCard title="Funnel mix" items={overview.funnels} loading={isLoading} />
        <Card variant="outlined" className={styles.insightCard}>
          <h3>Top competitors</h3>
          <ul className={styles.metricList}>
            {overview.competitors.length === 0 && <li>No competitor signal yet.</li>}
            {overview.competitors.map(([domain, hits]) => (
              <li key={domain}>
                <span>{domain}</span>
                <strong>{formatNumber(hits)} hits</strong>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function summaryStatusMessage(status?: string, error?: string | null) {
  if (status === 'failed') return userMessage(error, 'The summary could not be generated. Please try again.');
  if (status === 'disabled') return 'AI summaries are disabled for this deployment.';
  if (status === 'running') return 'AI summary is being generated now…';
  if (status === 'pending') return 'AI summary is queued. This panel will refresh automatically.';
  return 'Summary will appear here when OpenAI summary generation is enabled and complete.';
}

function InsightList({ title, items }: { title: string; items: unknown[] }) {
  return (
    <div>
      <h3>{title}</h3>
      <ul className={styles.summaryList}>
        {items.map((item, index) => (
          <li key={`${title}-${index}`}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
        ))}
      </ul>
    </div>
  );
}

function InsightCard({ title, items, loading }: { title: string; items: Record<string, number>; loading: boolean }) {
  const rows = Object.entries(items).sort((a, b) => b[1] - a[1]).slice(0, TOP_ITEM_LIMIT);
  return (
    <Card variant="outlined" className={styles.insightCard}>
      <h3>{title}</h3>
      <ul className={styles.metricList}>
        {loading && <li>Loading…</li>}
        {!loading && rows.length === 0 && <li>No signal yet.</li>}
        {rows.map(([label, count]) => (
          <li key={label}>
            <span>{label.replace(/_/g, ' ')}</span>
            <strong>{formatNumber(count)}</strong>
          </li>
        ))}
      </ul>
    </Card>
  );
}
